import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Paper, Box, Card, CardContent, Typography, Button, Grid, Divider, Avatar, Stack } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import { mainPageRootStyle } from '../styles';
import { loadAllPosts } from '../scheduler/actions';

function DashboardHome() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const posts = useSelector(state => state.scheduler.allPosts);
    const userId = useSelector(state => state.auth.user?.uid);

    useEffect(() => {
        document.title = 'Home - Sky Toolbox';
    }, []);

    useEffect(() => {
        if (userId) {
            dispatch(loadAllPosts(userId));
        }
    }, [dispatch, userId]);

    // Calculate stats
    const totalHandles = handles?.length || 0;
    const totalScheduledPosts = useMemo(() => {
        return (posts || []).filter(post => post.status === 'scheduled').length;
    }, [posts]);
    const postsWithErrors = useMemo(() => {
        return (posts || []).filter(post => post.status === 'failed') || [];
    }, [posts]);
    const totalPostsWithErrors = postsWithErrors.length;

    // Calculate scheduled posts per handle
    const postsPerHandle = useMemo(() => {
        if (!handles || !posts) return {};
        const counts = {};
        handles.forEach(handle => {
            counts[handle.handle] = (posts || []).filter(post => post.handle === handle.handle && post.status === 'scheduled').length;
        });
        return counts;
    }, [handles, posts]);

    // Calculate errors per handle
    const errorsPerHandle = useMemo(() => {
        if (!handles || !posts) return {};
        const counts = {};
        handles.forEach(handle => {
            counts[handle.handle] = (posts || []).filter(post => post.handle === handle.handle && post.status === 'failed').length;
        });
        return counts;
    }, [handles, posts]);

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            {/* Hero Section */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #1976d2 0%, #9c27b0 100%)',
                    borderRadius: 3,
                    p: 4,
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                    }
                }}
            >
                <Typography variant="h3" component="h1" fontWeight="bold" sx={{ mb: 1, position: 'relative' }}>
                    Welcome! 👋
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, position: 'relative' }}>
                    Your BlueSky automation hub
                </Typography>
            </Box>

            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 56, height: 56 }}>
                                    <PersonIcon fontSize="large" />
                                </Avatar>
                            </Box>
                            <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {totalHandles}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Total Handles
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(240, 147, 251, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 56, height: 56 }}>
                                    <CalendarTodayIcon fontSize="large" />
                                </Avatar>
                            </Box>
                            <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {totalScheduledPosts}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Scheduled Posts
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={0}
                        sx={{ 
                            background: totalPostsWithErrors > 0 
                                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                                : 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
                            color: 'white',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: totalPostsWithErrors > 0 
                                    ? '0 8px 24px rgba(255, 107, 107, 0.4)'
                                    : '0 8px 24px rgba(81, 207, 102, 0.4)'
                            }
                        }}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 56, height: 56 }}>
                                    <ErrorIcon fontSize="large" />
                                </Avatar>
                            </Box>
                            <Typography variant="h3" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {totalPostsWithErrors}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Posts with Errors
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                Quick Actions
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={1}
                        sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                            }
                        }}
                        onClick={() => navigate('/handles/add')}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'primary.main', mb: 2 }}>
                                <AddIcon />
                            </Avatar>
                            <Typography variant="h6" gutterBottom>
                                Add Handle
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Connect a new BlueSky account
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={1}
                        sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                            }
                        }}
                        onClick={() => navigate('/scheduler')}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'secondary.main', mb: 2 }}>
                                <EditIcon />
                            </Avatar>
                            <Typography variant="h6" gutterBottom>
                                Schedule Post
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Plan your content ahead
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card 
                        elevation={1}
                        sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                            }
                        }}
                        onClick={() => navigate('/growth')}
                    >
                        <CardContent>
                            <Avatar sx={{ bgcolor: 'success.main', mb: 2 }}>
                                <TrendingUpIcon />
                            </Avatar>
                            <Typography variant="h6" gutterBottom>
                                View Growth
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Track your metrics
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Handles Overview */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">
                    Your Handles
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/handles')}
                    size="small"
                >
                    Manage Handles
                </Button>
            </Box>

            {!handles || handles.length === 0 ? (
                <Card 
                    elevation={0}
                    sx={{ 
                        border: '2px dashed',
                        borderColor: 'divider',
                        bgcolor: 'grey.50'
                    }}
                >
                    <CardContent sx={{ textAlign: 'center', py: 8 }}>
                        <Avatar 
                            sx={{ 
                                width: 80, 
                                height: 80, 
                                bgcolor: 'primary.main',
                                mx: 'auto',
                                mb: 2
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 48 }} />
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            No handles yet
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                            Get started by adding your first BlueSky handle to unlock scheduling, growth tracking, and automation features
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/handles/add')}
                            sx={{ 
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem'
                            }}
                        >
                            Add Your First Handle
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {handles.map((handle) => (
                        <Grid item xs={12} sm={6} md={4} key={handle.id}>
                            <Card 
                                elevation={2}
                                sx={{ 
                                    height: '100%',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                                            <PersonIcon />
                                        </Avatar>
                                        <Typography variant="h6" fontWeight="bold">
                                            @{handle.handle}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Stats Section */}
                                    <Box 
                                        sx={{ 
                                            bgcolor: 'grey.50', 
                                            borderRadius: 2, 
                                            p: 2, 
                                            mb: 2,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box display="flex" flexDirection="column" alignItems="center">
                                                    <CalendarTodayIcon 
                                                        sx={{ 
                                                            fontSize: 32, 
                                                            color: 'primary.main',
                                                            mb: 0.5
                                                        }} 
                                                    />
                                                    <Typography variant="h4" fontWeight="bold" color="primary.main">
                                                        {postsPerHandle[handle.handle] || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                        Scheduled Posts
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box display="flex" flexDirection="column" alignItems="center">
                                                    <ErrorIcon 
                                                        sx={{ 
                                                            fontSize: 32, 
                                                            color: (errorsPerHandle[handle.handle] || 0) > 0 ? 'error.main' : 'text.disabled',
                                                            mb: 0.5
                                                        }} 
                                                    />
                                                    <Typography 
                                                        variant="h4" 
                                                        fontWeight="bold" 
                                                        color={(errorsPerHandle[handle.handle] || 0) > 0 ? 'error.main' : 'text.disabled'}
                                                    >
                                                        {errorsPerHandle[handle.handle] || 0}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                        Errors
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Stack spacing={1}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<TrendingUpIcon />}
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => navigate(`/growth?handle=${handle.handle}`)}
                                            sx={{ justifyContent: 'space-between' }}
                                        >
                                            View Growth
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<CalendarTodayIcon />}
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => navigate(`/scheduler?handle=${handle.handle}`)}
                                            sx={{ justifyContent: 'space-between' }}
                                        >
                                            Scheduler
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Paper>
    );
}

export default DashboardHome;

